import type { Ref } from 'vue'
import type { Middleware, MiddlewareData, Placement, Strategy } from '@floating-ui/vue'

import type {
    OffsetOptions,
    ShiftOptions,
    FlipOptions,
    ArrowOptions,
    SizeOptions,
    AutoPlacementOptions,
    HideOptions,
    InlineOptions,
} from '@floating-ui/vue'

import { ref } from 'vue'
import { useFloating, offset, shift, flip, arrow, size, autoPlacement, hide, inline, autoUpdate } from '@floating-ui/vue'

interface TooltipOptions {
    placement?: Placement
    strategy?: Strategy
    offset?: OffsetOptions
    shift?: ShiftOptions
    flip?: FlipOptions
    arrow?: ArrowOptions
    size?: SizeOptions
    autoPlacement?: AutoPlacementOptions
    hide?: HideOptions
    inline?: InlineOptions
}

interface TooltipComposable {
    reference: Ref<HTMLElement | null>
    tooltip: Ref<HTMLElement | null>
    isVisible: Ref<boolean>
    x: Ref<number | null>
    y: Ref<number | null>
    strategy: Ref<Strategy>
    middlewareData: MiddlewareData
    open: () => void
    close: () => void
}

export default function useTooltip(options: TooltipOptions = {}): TooltipComposable {
    const reference = ref<HTMLElement | null>(null)
    const tooltip = ref<HTMLElement | null>(null)
    const isVisible = ref(false)

    const middleware: Middleware[] = [
        offset(options.offset || 0),
        ...(options.shift ? [shift(options.shift)] : []),
        ...(options.flip ? [flip(options.flip)] : []),
        ...(options.arrow ? [arrow(options.arrow)] : []),
        ...(options.size ? [size(options.size)] : []),
        ...(options.autoPlacement ? [autoPlacement(options.autoPlacement)] : []),
        ...(options.hide ? [hide(options.hide)] : []),
        ...(options.inline ? [inline(options.inline)] : []),
    ]

    const { x, y, strategy, middlewareData, update } = useFloating(reference, tooltip, {
        placement: options.placement || 'left',
        strategy: options.strategy || 'absolute',
        middleware,
        whileElementsMounted: autoUpdate,
    })

    const open = () => {
        isVisible.value = true
        update()
    }

    const close = () => {
        isVisible.value = false
    }

    return {
        reference,
        tooltip,
        isVisible,
        x,
        y,
        strategy,
        middlewareData,
        open,
        close,
    }
}
